import React from "react";
import {
  APP_FORM_OPTIONS,
  getMaritalStatusCode,
  INPUT_TYPES,
  OPTION_VALUES,
  SMART_KID_OPTIONS,
} from "@ipru-common/constants/InputValues";
import { t } from "i18next";
import { calculateAnnualPremium, getStateIdByName } from "../SmartKidQuote/commonUtils";
import _ from "lodash";
import {
  cleanName,
  getCurrentProductFromMeta,
  getFullKycAddress,
  getNumericMonth,
  isProtectionPlan,
  hygieneString,
  splitFullName,
  updateDate,
  getActiveLanguage,
  getProductDetail,
  convertddMMYYToddMmmYyyy,
} from "../CommonFunctions";
import { STATES } from "@ipru-common/constants/state";
import {
  ENGLISH,
  FALSY,
  LANGUAGES,
  SIGNATURE_SWP,
  TRULY,
  showReasonForFieldsValue,
} from "@ipru-common/constants/SiteConstants";
import { allHealthQuestions, NON_RIDER_OCCUPATIONS, SELF_EMPLOYED_LIST } from "@ipru-common/constants/SavingsAppForm";
import { RIDER_DATA, RIDER_TYPES } from "@ipru-common/constants/SignatureUlipRider";
import CalculatorFormStore from "@ipru-common/store/CalculatorFormStore";
import QuoteFormStore from "@ipru-common/store/QuoteFormStore";
import BmiIndex from "@ipru-common/constants/savingsAppForm/bmiIndex";
import { PRODUCT_DETAILS, PRODUCT_KEYS } from "@ipru-common/constants/products";
import GlobalStore from "@ipru-common/store/GlobalStore";
import {
  AddressTooltip,
  NriAnnualIncomeTooltip,
  NriAnnualIncomeTooltipGiftCity,
} from "@ipru-common/components/SavingAppForm/Components/SectionMapperComponents/AppFormToolTips";
import { REGEX } from "@ipru-common/constants/regex";
import {
  noLeadingTrailingSpaces,
  noSpecialCharactersValidator,
  validateOnlyOneSpace,
} from "@ipru-common/libs/ValidationSchemas/CustomRules";

export const getHeightFeetOptions = () => {
  const options = [];

  for (let feet = 2; feet <= 7; feet++) {
    const feetToCm = feet * 12 * 2.54;
    options.push({
      id: `${feet} feet`,
      name: `${feet} feet`,
      cm: Math.round(feetToCm),
    });
    for (let inches = 1; inches <= 11; inches++) {
      const feetInchToCm = feetToCm + inches * 2.54;
      options.push({
        id: `${feet} feet ${inches} inches`,
        name: `${feet} feet ${inches} inch${inches !== 1 ? "es" : ""}`,
        cm: Math.round(feetInchToCm),
      });
    }
  }
  options.push({
    id: `7 feet 11 inches`,
    name: `7 feet 11 inches`,
    cm: 241,
  });

  return options;
};

export const getFeetOptions = () => {
  const options = [];

  for (let feet = 2; feet <= 8; feet++) {
    options.push({
      id: feet,
      name: `${feet} ft`,
      feet,
    });
  }

  return options;
};

export const getInchOptions = () => {
  const options = [];

  for (let inches = 0; inches <= 11; inches++) {
    options.push({
      id: inches,
      name: `${inches} in`,
      inches,
    });
  }

  return options;
};

export const getSplitNames = (fullName, removePrefix) => {
  let names = {
    firstName: "",
    lastName: "",
  };

  if (!fullName || typeof fullName !== "string") {
    return names;
  }
  let nameToProcess = fullName || "";
  if (removePrefix) {
    nameToProcess = cleanName(nameToProcess);
  }

  const nameParts = nameToProcess?.split(" ") || [""];

  if (nameParts.length === 1) {
    names.firstName = nameParts[0];
    return names;
  }
  names.lastName = nameParts.pop() || "";
  names.firstName = nameParts.join(" ") || "";

  return names;
};

export const getMultiSplitNames = (fullName) => {
  let names = {
    firstName: "",
    midName: "",
    lastName: "",
  };

  if (!fullName || typeof fullName !== "string") {
    return names;
  }
  let nameToProcess = fullName || "";

  nameToProcess = cleanName(nameToProcess);

  const nameParts = nameToProcess?.split(" ") || [""];

  if (nameParts.length === 1) {
    names.firstName = nameParts[0];
  } else if (nameParts.length === 2) {
    names.firstName = nameParts[0];
    names.lastName = nameParts[1];
  } else if (nameParts.length >= 3) {
    names.firstName = nameParts[0];
    names.lastName = nameParts[nameParts.length - 1];
    names.midName = nameParts.slice(1, -1).join(" ");
  }

  return names;
};

export const getFullName = (names = []) => {
  return names.filter((name) => name).join(" ");
};

export const getHeightSplit = (heightFeet) => {
  const splitHeight = heightFeet?.split(" ");
  let height = {
    feet: "",
    inch: "",
  };
  if (splitHeight?.length === 4) {
    return {
      feet: splitHeight[0],
      inch: splitHeight[2],
    };
  } else if (splitHeight?.length < 4) {
    return {
      feet: splitHeight[0],
      inch: 0,
    };
  }
  return height;
};

export const getAppointeeRelation = (relationId) => {
  return APP_FORM_OPTIONS.APPOINTEE_RELATIONSHIP?.find(({ id }) => String(id) === String(relationId))?.id || "";
};

export const getCurrentTimeStamp = () => {
  const time = new Date();
  return time;
};

export const getKeyByValue = (object = {}, value = "") => {
  return Object?.keys(object).find((key) => object[key] === value);
};

export const getAllocationFunds = (allocationFunds) => {
  const funds = {};
  allocationFunds?.forEach((fund, i) => {
    funds[`fund${i + 1}`] = {
      Name: fund?.name,
      Value: fund?.value,
    };
  });
  return funds;
};

export const getPartnerApiFunds = (fundsData) => {
  let fundsObject = {};
  Object?.keys(fundsData)?.forEach((key, i) => {
    const name = key?.replace(/_/g, " ") || "";
    const value = Number(fundsData[key] || 0);
    fundsObject[`fund${i + 1}`] = {
      Name: name,
      Value: value,
    };
  });

  return fundsObject;
};

export const getDropOffApiFunds = (fundsData) => {
  let funds = {};
  fundsData?.forEach(({ name, value }) => {
    let keyName = name?.replace(/ /g, "_");
    funds = {
      ...funds,
      [keyName]: value,
    };
  });

  return funds;
};

export const trueFalse = (val) => {
  const inputValue = val?.toLowerCase();

  if (TRULY.includes(inputValue)) {
    return OPTION_VALUES.TRUE;
  } else if (FALSY.includes(inputValue)) {
    return OPTION_VALUES.FALSE;
  } else {
    return "";
  }
};

export const yesNo = (val) => (val ? "Yes" : "No");

export const getYesNoValue = (value) => {
  if (value === true) return "YES";

  if (typeof value === "string") {
    const normalized = value.trim().toUpperCase();
    return ["Y", "YES"].includes(normalized) ? "YES" : "NO";
  }

  return "NO";
};

export const yesToY = (val) => (val === OPTION_VALUES?.Y_YES ? OPTION_VALUES?.YES : OPTION_VALUES?.NO);

export const ekycNullFunction = ({ token, flag } = {}) => {
  if (token !== "" && !flag) {
    return null;
  } else {
    if (flag) {
      return "Y";
    } else return "N";
  }
};

// for resume journey to convert from dropOff to appForm
export const getOccupationProfession = ({ occupation, profession } = {}) => {
  if (occupation !== OPTION_VALUES.OCCUPATION.PROFESSIONAL) return "";

  return APP_FORM_OPTIONS.MY_PROFESSION.find(({ id }) => id === profession)?.id || "";
};

export const getOccupationDes = ({ occupation, my_profession } = {}) => {
  let occupationOptions =
    occupation === OPTION_VALUES.OCCUPATION.PROFESSIONAL
      ? APP_FORM_OPTIONS?.MY_PROFESSION
      : APP_FORM_OPTIONS?.SELF_EMPLOYED;

  let requiredOccupation = occupation === OPTION_VALUES.OCCUPATION.PROFESSIONAL ? my_profession : occupation;

  if (!requiredOccupation || !occupationOptions) return "";
  const result = occupationOptions?.find((occu) => {
    return occu.id === requiredOccupation;
  });
  return result?.name || "";
};

function getNameOfOrg(self) {
  switch (self) {
    case OPTION_VALUES.OCCUPATION.SELF_EMPLOYEE_BUSINESSMAN:
      return "Others";
    default:
      return "";
  }
}

function getNameOfOrgDesc(self) {
  switch (self) {
    case OPTION_VALUES.OCCUPATION.SELF_EMPLOYEE_BUSINESSMAN:
      return "Others";
    default:
      return "";
  }
}

function getMyProfession(self) {
  switch (self) {
    case OPTION_VALUES.OCCUPATION.SELF_EMPLOYEE_CA:
      return OPTION_VALUES.OCCUPATION.CA;
    case OPTION_VALUES.OCCUPATION.SELF_EMPLOYEE_DOCTOR:
      return OPTION_VALUES.OCCUPATION.DOCTOR;
    case OPTION_VALUES.OCCUPATION.SELF_EMPLOYEE_LAWYER:
      return OPTION_VALUES.OCCUPATION.LAWYER;
    case OPTION_VALUES.OCCUPATION.SELF_EMPLOYEE_ARCHITECT:
      return OPTION_VALUES.OCCUPATION.OTHER;
    case OPTION_VALUES.OCCUPATION.SELF_EMPLOYEE_CONSULTANT:
      return OPTION_VALUES.OCCUPATION.OTHER;
    default:
      return "";
  }
}

function getOrgType(self, isProposer, isSelfJourney) {
  switch (self) {
    case OPTION_VALUES.OCCUPATION.SELF_EMPLOYEE_BUSINESSMAN:
      return isSelfJourney ? "NOT ANSWERED" : isProposer ? "NOT ANSWERED" : undefined;
    default:
      return isSelfJourney ? "" : isProposer ? "" : undefined;
  }
}

function getOrgTypeDesc(self, isProposer, isSelfJourney) {
  switch (self) {
    case OPTION_VALUES.OCCUPATION.SELF_EMPLOYEE_BUSINESSMAN:
      return isSelfJourney ? "Others" : isProposer ? "Others" : undefined;
    default:
      return isSelfJourney ? "" : isProposer ? "" : undefined;
  }
}

export const getPartnerOccupationDetails = ({
  occupation,
  organisation_name,
  organisation_type,
  others_organisation_type,
  my_profession,
  orgList,
  isProposer,
}) => {
  /**
   * @typedef {object} OccDetails
   * @property {string} occ
   * @property {string} orgType
   * @property {string} orgTypeDesc
   * @property {string} myProf
   * @property {string} occDesc
   * @property {string} nameOfOrg
   * @property {string} nameOfOrgDesc
   */
  // one for la and one for proposer

  let occDetails = {
    occ: occupation,
    orgType: "",
    orgTypeDesc: "",
    myProf: "",
    occDesc: "",
    nameOfOrg: "", //17-03,
    nameOfOrgDesc: "", // note nameOfOrgDescFormat() discuss
  };
  const isSelfJourney = !CalculatorFormStore.checkIsProposer();

  if (occupation === OPTION_VALUES.OCCUPATION.SALARIED) {
    const isFromList = orgList.includes(organisation_name);
    if (isFromList) {
      occDetails.nameOfOrg = organisation_name;
      occDetails.nameOfOrgDesc = "";
    } else {
      occDetails.nameOfOrg = OPTION_VALUES.ORGANISATION_NAME_OTHERS;
      occDetails.nameOfOrgDesc = organisation_name;
    }
    occDetails.orgType = organisation_type;
    occDetails.orgTypeDesc = others_organisation_type;
  } else if (occupation === OPTION_VALUES.OCCUPATION.PROFESSIONAL) {
    let occupationOptions = APP_FORM_OPTIONS?.MY_PROFESSION;

    let requiredOccupation = occupation === OPTION_VALUES.OCCUPATION.PROFESSIONAL ? my_profession : occupation;

    if (!requiredOccupation || !occupationOptions) {
      occDetails.myProf = "";
    } else {
      occDetails.myProf =
        occupationOptions?.find((occu) => {
          return occu.id === requiredOccupation;
        }).id || "";
    }
  } else if (SELF_EMPLOYED_LIST.includes(occupation)) {
    occDetails = {
      occ: occupation,
      nameOfOrg: getNameOfOrg(occupation),
      nameOfOrgDesc: getNameOfOrgDesc(occupation, isSelfJourney),
      myProf: getMyProfession(occupation),
      orgType: getOrgType(occupation, isProposer, isSelfJourney),
      occDesc: "",
      orgTypeDesc: getOrgTypeDesc(occupation, isProposer, isSelfJourney),
    };
  } else if (NON_RIDER_OCCUPATIONS?.includes(occupation)) {
    occDetails = {
      occ: occupation,
      nameOfOrg: "",
      myProf: "",
      nameOfOrgDesc: "",
      occDesc: "",
      orgTypeDesc: "",
    };
  }
  if (organisation_type !== OPTION_VALUES.ORGANISATION_TYPE.NOT_ANSWERED) {
    if (occupation !== OPTION_VALUES.OCCUPATION.SELF_EMPLOYEE_BUSINESSMAN) {
      occDetails.orgTypeDesc = "";
    }
  }
  /**@type {OccDetails} */
  return occDetails;
};

export const getDropOccupationDetails = ({
  occupation,
  organisation_name,
  organisation_type,
  others_organisation_type,
  my_profession,
  orgList,
  isProposer,
}) => {
  /**
   * @typedef {object} OccDetails
   * @property {string} occ
   * @property {string} orgType
   * @property {string} orgTypeDesc
   * @property {string} myProf
   * @property {string} occDesc
   * @property {string} nameOfOrg
   * @property {string} nameOfOrgDesc
   */
  // one for la and one for proposer

  let occDetails = {
    occ: occupation,
    orgType: organisation_type,
    orgTypeDesc: others_organisation_type,
    myProf: "",
    occDesc: "",
    nameOfOrg: "", //17-03,
    nameOfOrgDesc: "", // note nameOfOrgDescFormat() discuss
  };
  const isSelfJourney = !CalculatorFormStore.checkIsProposer();

  if (occupation === OPTION_VALUES.OCCUPATION.SALARIED) {
    const isFromList = orgList.includes(organisation_name);
    if (isFromList) {
      occDetails.nameOfOrg = organisation_name;
      occDetails.nameOfOrgDesc = "";
    } else {
      occDetails.nameOfOrg = OPTION_VALUES.ORGANISATION_NAME_OTHERS;
      occDetails.nameOfOrgDesc = organisation_name;
    }
  } else if (occupation === OPTION_VALUES.OCCUPATION.PROFESSIONAL) {
    let occupationOptions = APP_FORM_OPTIONS?.MY_PROFESSION;

    let requiredOccupation = occupation === OPTION_VALUES.OCCUPATION.PROFESSIONAL ? my_profession : occupation;

    if (!requiredOccupation || !occupationOptions) {
      occDetails.myProf = "";
    } else {
      occDetails.myProf =
        occupationOptions?.find((occu) => {
          return occu.id === requiredOccupation;
        }).id || "";
    }
  } else if (SELF_EMPLOYED_LIST.includes(occupation)) {
    occDetails = {
      occ: occupation,
      nameOfOrg: getNameOfOrg(occupation),
      nameOfOrgDesc: getNameOfOrgDesc(occupation, isSelfJourney),
      myProf: getMyProfession(occupation),
      orgType: getOrgType(occupation, isProposer, isSelfJourney),
      occDesc: "",
      orgTypeDesc: getOrgTypeDesc(occupation, isProposer, isSelfJourney),
    };
  } else if (NON_RIDER_OCCUPATIONS?.includes(occupation)) {
    occDetails = {
      occ: occupation,
      nameOfOrg: "",
      myProf: "",
      nameOfOrgDesc: "",
      occDesc: "",
      orgTypeDesc: "",
    };
  }
  if (organisation_type !== OPTION_VALUES.ORGANISATION_TYPE.NOT_ANSWERED) {
    if (occupation !== OPTION_VALUES.OCCUPATION.SELF_EMPLOYEE_BUSINESSMAN) {
      occDetails.orgTypeDesc = "";
    }
  }
  /**@type {OccDetails} */
  return occDetails;
};

/**
 *
 * @param {string} date
 * @return {string}
 */
export const getExperianDateFormat = (dateString) => {
  if (!dateString) {
    return "";
  }

  // Replace all separators with spaces {add}
  const standardizedDateString = dateString.replace(/[-./,]/g, " ");

  let dateArray = standardizedDateString?.split(" ");
  const [date, month, year] = dateArray;
  return `${year || ""}${month || ""}${date || ""}`;
};

/**
 *
 * @param {{name : string, gender : string | number, maritalStatus : string}} param0
 * @returns {string}
 */
export const getNameWithPrefix = ({ name, gender, maritalStatus }) => {
  let prefix = "";

  switch (gender) {
    case "Male":
      prefix = "Mr.";
      break;
    case "Female":
      switch (maritalStatus) {
        case OPTION_VALUES.MARITAL_STATUS.MARRIED:
          prefix = "Mrs.";
          break;
        case OPTION_VALUES.MARITAL_STATUS.SINGLE:
          prefix = "Ms.";
          break;
        default:
          prefix = "Ms.";
      }
      break;
    default:
      prefix = "";
  }

  return `${prefix} ${name?.toLowerCase()}`;
};

/**
 *
 * @param {any} data
 * @returns {PersonalDetails}
 */
export const fillPersonalDetails = (data) => {
  const response = {};

  response.full_name = `${data?.firstname || ""} ${data?.lastname || ""}`;
  if (data?.maritialStatus) {
    const allMaritalStatus = getMaritalStatusCode();
    if (allMaritalStatus?.[data.maritialStatus]) response.marital_status = allMaritalStatus?.[data.maritialStatus];
  }

  response.mobile_number = data?.mobileNumber || "";

  const { AGE_PROOF, IDENTITY_PROOF, ADDRESS_PROOF, HIGHEST_EDUCATION } = OPTION_VALUES;

  if (data?.addressproofLongDescription) {
    const proof = _.findKey(
      ADDRESS_PROOF,
      (value) => value?.toLowerCase() === data?.addressproofLongDescription?.toLowerCase()
    );
    if (proof) {
      response.address_proof = ADDRESS_PROOF[proof];
    }
  }
  if (data?.idproofLongDescription) {
    const proof = _.findKey(
      IDENTITY_PROOF,
      (value) => value?.toLowerCase() === data?.idproofLongDescription?.toLowerCase()
    );
    if (proof) {
      response.identity_proof = IDENTITY_PROOF[proof];
    }
  }
  if (data?.ageProofShortDescription) {
    const proof = _.findKey(
      AGE_PROOF,
      (value) => value?.toLowerCase() === data?.ageProofShortDescription?.toLowerCase()
    );
    if (proof) {
      response.age_proof = AGE_PROOF[proof];
    }
  }

  if (data?.education) {
    const education = _.findKey(HIGHEST_EDUCATION, (value) => value?.toLowerCase() === data?.education?.toLowerCase());
    if (education) {
      response.highest_education = HIGHEST_EDUCATION[education] || "";
    }
  }

  const full_address = getFullKycAddress({
    line1: data?.line1 === "-" ? "" : data?.line1,
    line2: data?.line2 === "-" ? "" : data?.line2,
    line3: data?.line3 === "-" ? "" : data?.line3,
  });
  const pincode = data?.pincode || "";
  const landmark = data?.landmark.replace("-", "") || "";
  const city = data?.line4 || "";
  const state = getStateIdByName(data?.line5, STATES) || "";

  response.full_address = full_address;
  response.pincode = pincode;
  response.landmark = landmark;
  response.city = city;
  response.state = state;

  response.per_full_address = full_address;
  response.per_pincode = pincode;
  response.per_landmark = landmark;
  response.per_city = city;
  response.per_state = state;

  response.annual_income = data?.annualIncome;
  response.pan_number = data?.pancardno || "";

  return response;
};

/**
 * @typedef {object} checkNomineeArg
 * @property {string} maritalStatus
 * @property {string} relation
 */

/**
 *
 * @param {checkNomineeArg}
 * @returns {boolean}
 */
export const checkNomineeReasonRequired = ({ maritalStatus, relation }) => {
  let nonBloodRelations = Object.values(OPTION_VALUES.NOMINEE_RELATIONSHIP_OTHERS_REASON);

  if (maritalStatus === OPTION_VALUES.MARITAL_STATUS.MARRIED) {
    nonBloodRelations = [
      ...nonBloodRelations,
      OPTION_VALUES.NOMINEE_RELATIONSHIP.BROTHER,
      OPTION_VALUES.NOMINEE_RELATIONSHIP.SISTER,
    ];
  }

  const isRequired = nonBloodRelations.includes(relation);
  return isRequired;
};

export const checkNriLaPerAddress = ({
  is_address_similar,
  isNRIBool,
  isProposerBool,
  LA_Prop_PerAdd_chk,
  Prop_IndianAdd_chk,
  la_nationality_status,
  is_prop_indian,
}) => {
  if (!isProposerBool) {
    // If there's no proposer, return based on NRI status
    return isNRIBool ? false : !is_address_similar;
  }
  // Proposer logic
  if (isNRIBool) {
    // Check if Life Assured is also NRI
    const isLaNri = la_nationality_status === OPTION_VALUES.NATIONALITY.NONINDIAN || !is_prop_indian;
    return isLaNri ? false : !Prop_IndianAdd_chk;
  }
  // If not NRI, return based on the common address check
  return !LA_Prop_PerAdd_chk;
};

export const checkLaInternationalAdd = ({
  isNRIBool,
  isProposerBool,
  la_nationality_status,
  is_prop_indian,
  Prop_InternationalAdd_chk,
}) => {
  if (!isProposerBool) {
    return isNRIBool;
  }

  if (isNRIBool) {
    const isLaNri = la_nationality_status === OPTION_VALUES.NATIONALITY.NONINDIAN || !is_prop_indian;
    return isLaNri ? !Prop_InternationalAdd_chk : false;
  }
  return false;
};

export const checkLaCommAddress = ({
  isProposerBool,
  isNRIBool,
  Prop_InternationalAdd_chk,
  LA_Prop_ComAdd_chk,
  Prop_IndianAdd_chk,
  la_nationality_status,
  is_prop_indian,
}) => {
  if (!isProposerBool) {
    // If there's no proposer, return based on NRI status
    return !isNRIBool;
  }
  // Proposer logic
  if (isNRIBool) {
    // Check if Life Assured is also NRI
    const isLaNri = la_nationality_status === OPTION_VALUES.NATIONALITY.NONINDIAN || !is_prop_indian;
    return isLaNri ? !Prop_IndianAdd_chk : !Prop_InternationalAdd_chk;
  }
  // If not NRI, return based on the common address check
  return !LA_Prop_ComAdd_chk;
};

/**
 *
 * @param {SavingsQuoteForm} quoteData
 */

export const getSavingsSwpString = (quoteData) => {
  let yrs = [];
  let pay_per = [];
  // eslint-disable-next-line no-unused-vars
  Array.from({ length: 5 }).forEach((_, index) => {
    const currentPay = quoteData?.swp?.payoutDetails?.[index];
    yrs.push(String(currentPay?.payoutYear || "0"));
    pay_per.push(String(currentPay?.payoutPercentage || "0"));
  });

  const swp = JSON.stringify({
    SWP: quoteData?.swpExpand,
    SWP_mode: quoteData?.swp?.paymentMode,
    SWP_payDate: quoteData?.swp?.paymentDate,
    year: yrs,
    payout_per: pay_per,
    retStart_Yr: "",
    ret_payout_per: "4",
    SWP_payMonth: getNumericMonth(quoteData?.swp?.paymentMonth),
  });

  return swp;
};

export const getSavingsSwpObject = (swp) => {
  const swpObject = JSON.parse(swp);

  const paymentDetails = swpObject?.year?.reduce((acc, payYear, i) => {
    const currentPer = swpObject?.payout_per?.[i];
    if (Number(currentPer) && Number(payYear)) {
      acc.push({
        payoutYear: Number(payYear),
        payoutPercentage: Number(currentPer),
      });
    }
    return acc;
  }, []);

  return {
    swpFlag: swpObject?.SWP,
    swp: {
      paymentMode: swpObject.SWP_mode || "",
      paymentDate: swpObject.SWP_payDate || "",
      paymentMonth: swpObject.paymentMonth || "",
      payoutDetails: paymentDetails?.length > 0 ? paymentDetails : [{ payoutYear: "", payoutPercentage: "" }],
    },
  };
};

/**
 *
 * @param {SavingsQuoteForm['swp']} swp
 */
export const getDoItYourSelf = (swp, goal) => {
  const doItYourSelfYears = {};
  const doItYourSelfPayOuts = {};

  const data = Array.from({ length: 5 }).map((_, i) => {
    const payoutYear = goal === SIGNATURE_SWP.DOITYOURSELF ? swp?.payoutDetails?.[i]?.payoutYear || 0 : 0;
    const payout =
      payoutYear === 0 || goal !== SIGNATURE_SWP.DOITYOURSELF ? 0 : swp?.payoutDetails?.[i]?.payoutPercentage || 0;

    return {
      year: payoutYear,
      payout: payout,
    };
  });

  // Sort by year, but push 0 values to the last
  data.sort((a, b) => {
    if (a.year == 0) return 1; // Move `0` values to the last
    if (b.year == 0) return -1;
    return a.year - b.year; // Otherwise, sort normally
  });

  // Reconstruct doItYourSelfYears and doItYourSelfPayOuts
  data.forEach((item, sortedIndex) => {
    doItYourSelfYears[`DoItYourSelfYear${sortedIndex + 1}`] = item.year;
    doItYourSelfPayOuts[`DoItYourSelfPayOut${sortedIndex + 1}`] = item.payout;
  });

  return {
    ...doItYourSelfYears,
    ...doItYourSelfPayOuts,
  };
};

/**
 *
 * @param {SavingsQuoteForm} props
 */
export const getRidersStrings = (quoteData) => {
  const ridersArray = quoteData?.calculatedData?.riders
    ?.map((currRider) => {
      const isSelectedRider = quoteData.selectedRiders.find(
        (selRider) => selRider.sectionName === currRider.Name && selRider.Premium === currRider.Premium
      );

      if (isSelectedRider) {
        return {
          ...currRider,
          isSelected: true,
        };
      } else {
        return currRider;
      }
    })
    .reduce((acc, current) => {
      const existing = acc.find((item) => item.Name === current.Name);
      if (!existing) {
        // Add the item if it doesn't already exist
        acc.push(current);
      } else if (!existing?.isSelected && current?.isSelected) {
        // Replace existing item if current item is selected
        acc = acc.map((item) => (item?.Name === current?.Name ? current : item));
      }
      return acc;
    }, [])
    .sort((a, b) => (b?.isSelected ? 1 : 0) - (a?.isSelected ? 1 : 0));

  return { ridersArray };
};

export const getDropOffRiders = (ridersArray = []) => {
  const dropRiders = {};
  ridersArray.forEach((finalRider, i) => {
    dropRiders[`rider${i + 1}`] = JSON.stringify(
      finalRider.isSelected
        ? {
            name: finalRider?.Name || "",
            sumAssured: finalRider?.SA || 0,
            term: finalRider?.Term || 0,
            Premium: finalRider?.Premium || 0,
            ServiceTax: finalRider?.ServiceTax || 0,
            Percentage: Number(finalRider.Percentage) || 0,
          }
        : {
            name: finalRider?.Name || "",
            sumAssured: 0,
            term: 0,
            Premium: 0,
            ServiceTax: 0,
          }
    );
  });

  return dropRiders;
};

// export const getPartnerRiders = (ridersArray = [],annualIncome) => {
//   const partnerRider = {};
//   const annualIncomeCondition = annualIncome < +OPTION_VALUES.ANNUAL_INCOME.THREE_L;
//   ridersArray.forEach((finalRider, i) => {
//     partnerRider[`rider${i + 1}`] = finalRider.isSelected && !annualIncomeCondition
//       ? {
//           name: finalRider?.Name || "",
//           sumAssured: finalRider?.SA || 0,
//           term: finalRider?.Term || 0,
//           percentage: Number(finalRider.Percentage) || 0,
//           // Premium: finalRider?.Premium || 0,
//           // ServiceTax: finalRider?.ServiceTax || 0,
//         }
//       : {
//           name: finalRider?.Name || "",
//           sumAssured: 0,
//           term: 0,
//           // Premium: 0,
//           // ServiceTax: 0,
//         };
//   });
//   return partnerRider;
// };

export const getPartnerRidersFromSelectedRiders = (selectedRiders = [], annualIncome) => {
  const partnerRider = {};
  const annualPremium = calculateAnnualPremium({
    invest: QuoteFormStore?.quoteFormData?.invest,
    paymentFrequency: QuoteFormStore?.quoteFormData?.paymentFrequency,
  });

  const { isWopSelected } = getWOPOption(selectedRiders);
  const annualIncomeCondition = annualIncome < +OPTION_VALUES.ANNUAL_INCOME.THREE_L && !isWopSelected;
  const {
    appFormData: { personalDetails },
  } = GlobalStore.getSnapshot();
  const isProposer = CalculatorFormStore.checkIsProposer();
  const nonRiderOccupation = NON_RIDER_OCCUPATIONS.includes(
    isProposer ? personalDetails?.propDetails?.propocc : personalDetails?.occupation
  );
  if (annualIncomeCondition || nonRiderOccupation) {
    return partnerRider;
  }
  selectedRiders?.forEach((rider, i) => {
    const riderData = {
      name: rider?.sectionName || "",
      sumAssured: Math.min(isWopSelected ? annualPremium : rider?.SA, 30000000),
      term: isWopSelected ? QuoteFormStore?.quoteFormData?.ppt : QuoteFormStore?.quoteFormData?.term || 0,
    };

    if (rider.sectionName !== RIDER_TYPES.BTBB && rider.percentage) {
      riderData.percentage = [15000000, 30000000].includes(rider.SA) ? 3 : Number(rider.percentage) * 100;
    }

    partnerRider[`rider${i + 1}`] = riderData;
  });
  return partnerRider;
};

export const getSavingsRidersArray = (productData) => {
  const ridersArray = [];

  let index = 1;
  while (productData[`rider${index}`]) {
    const currentRider = JSON.parse(productData[`rider${index}`]);

    ridersArray.push({
      Name: currentRider?.name || "",
      SA: currentRider?.sumAssured || "",
      Term: currentRider?.term || "",
      Premium: currentRider?.Premium || "",
      ServiceTax: currentRider?.ServiceTax || "",
      Percentage: currentRider?.Percentage || 0,
    });
    index++;
  }

  const selectedRiderArray =
    ridersArray
      .filter((currRider) => Number(currRider?.Premium) !== 0)
      .map((filteredRider) => {
        return {
          sectionName: filteredRider?.Name,
          product: "",
          percentage: filteredRider?.Percentage,
          SA: filteredRider?.SA,
          Premium: filteredRider?.Premium,
          ServiceTax: filteredRider?.ServiceTax,
        };
      }) || [];

  const riderPremium =
    selectedRiderArray?.reduce(
      (acc, selRider) => acc + selRider.Premium + (isProtectionPlan() ? selRider.ServiceTax : 0),
      0
    ) || 0;

  return { allRiders: ridersArray, selectedRiders: selectedRiderArray, riderPremium };
};

export const getFormattedDate = (date = []) => {
  if (!date) {
    return "";
  }
  const [day, month, year] = date.split("/");
  return [month, day, year].join("/");
};

export const getSavingsPremium = ({ occupation, selectedRiders, invest, riderPremium, annualIncome }) => {
  const filteredRiders = selectedRiders?.filter((rider) => rider.Premium) || [];
  const isRiderSelected =
    filteredRiders?.length > 0 || QuoteFormStore.quoteFormData.riderPremium ? OPTION_VALUES.TRUE : OPTION_VALUES.FALSE;
  const isNonRiderOccupation = NON_RIDER_OCCUPATIONS.includes(occupation);
  const { isWopSelected } = getWOPOption(selectedRiders);

  const annualIncomeCondition = annualIncome < +OPTION_VALUES.ANNUAL_INCOME.THREE_L && !isWopSelected;

  const riderPremiumVal =
    isRiderSelected === OPTION_VALUES.TRUE && !isNonRiderOccupation && riderPremium && !annualIncomeCondition
      ? Number(riderPremium)
      : 0;
  return Number(invest + riderPremiumVal);
};

export const getWOPOption = (selectedRiders) => {
  // console.log({selectedRiders});
  let isWopSelected = false;
  let wopOption = null;
  const wopRider = selectedRiders?.find((current) => current?.sectionName === RIDER_TYPES.WOP);
  if (wopRider) {
    isWopSelected = true;
    wopOption = RIDER_DATA[wopRider?.product]?.value;
  }
  return { isWopSelected, wopOption };
};

export const isBMIValid = ({ weight, heightInInches }) => {
  const currentProduct = getCurrentProductFromMeta();
  if (!BmiIndex[currentProduct]) {
    return true;
  }
  const { min, max } = BmiIndex[currentProduct];
  const heightInMeter = heightInInches * 0.0254;
  const bmi = weight / (heightInMeter * heightInMeter);

  return bmi >= min && bmi <= max;
};

export const getHeightInInches = (height_feet) => {
  const height = getHeightSplit(height_feet);
  return +height?.feet * 12 + +height?.inch;
};

export const getProductByKey = (key) => {
  return PRODUCT_DETAILS.find((product) => product.key === key);
};

const getEmptyNomineeDetails = () => ({
  gender: "",
  frstNm: "",
  relationship: "",
  dob: "",
  lstNm: "",
  reasonNonBloodRelation: "",
  otherReasonForNonBloodRelation: "",
  apnteDtls: {
    gender: "",
    frstNm: "",
    lstNm: "",
    relationship: "",
    dob: "",
  },
});

const getNomineeDetails = (pDetails, nomineeName, isReasonRequired) => ({
  gender: pDetails?.nominee_gender ?? "",
  frstNm: hygieneString(nomineeName?.firstName ?? ""),
  lstNm: hygieneString(nomineeName?.lastName ?? ""),
  relationship: pDetails?.nominee_relationship ?? "",
  dob: pDetails?.nominee_dob ? updateDate(pDetails.nominee_dob) : "",
  reasonNonBloodRelation: isReasonRequired ? (pDetails?.nominee_declaration ?? "") : "",
  otherReasonForNonBloodRelation:
    isReasonRequired && pDetails?.nominee_declaration === OPTION_VALUES.NON_BLOOD_RELATION_REASON.OTHER
      ? (pDetails?.nominee_other_reason ?? "")
      : "",
});

const getAppointeeDetails = (pDetails) => {
  const appointeeName = splitFullName(pDetails?.appointee_fullname);
  return {
    gender: pDetails?.appointee_gender ?? "",
    frstNm: appointeeName?.firstName ?? "",
    lstNm: appointeeName?.lastName ?? "",
    relationship: getAppointeeRelation(pDetails?.appointee_relationship) ?? "",
    dob: pDetails?.appointee_dob ? updateDate(pDetails.appointee_dob) : "",
    mobile: pDetails?.appointee_mobile_number ?? "",
    email: pDetails?.appointee_email_id ?? "",
    bankAccNo: pDetails?.nominee?.account_number ?? "",
    ifsc: pDetails?.nominee?.ifsc_code ?? "",
    accountType: pDetails?.nominee?.account_type === OPTION_VALUES?.ACCOUNT_TYPE.SAVING ? "Savings" : "Current",
  };
};

const getNonMinorNomineeBankDetails = (pDetails) => ({
  mobile: pDetails?.nominee_mobile_number ?? "",
  email: pDetails?.nominee_email_id ?? "",
  bankAccNo: pDetails?.nominee?.account_number ?? "",
  ifsc: pDetails?.nominee?.ifsc_code ?? "",
  accountType: pDetails?.nominee?.account_type === OPTION_VALUES?.ACCOUNT_TYPE.SAVING ? "Savings" : "Current",
  aptnDtls: {
    gender: "",
    frstNm: "",
    lstNm: "",
    relationship: "",
    dob: "",
  },
});

const getNomineeDetailsFromProposer = (pDetails) => {
  let nomineeInfo = getEmptyNomineeDetails();
  const propDetails = pDetails?.propDetails;
  const propName = splitFullName(propDetails?.propFullNm);
  const proposerRelationWithLA = propDetails?.propRel;
  nomineeInfo = {
    ...nomineeInfo,
    frstNm: hygieneString(propName?.firstName),
    lstNm: hygieneString(propName?.lastName),
    gender: propDetails?.propGender ?? "",
    relationship: proposerRelationWithLA,
    dob: propDetails?.propDob ? updateDate(propDetails.propDob) : "",
  };
  return nomineeInfo;
};

export const getMultipleNomineeInfo = ({ pDetails, dobToPartnerFormat = true }) => {
  const nominees = pDetails?.nominees || [];

  return nominees.map((nominee) => {
    const nomineeName = splitFullName(nominee.nominee_fullname);

    const isReasonRequired = checkNomineeReasonRequired({
      maritalStatus: pDetails?.marital_status,
      relation: nominee.nominee_relationship,
    });

    const nomineeInfo = {
      frstNm: nomineeName?.firstName,
      lstNm: nomineeName?.lastName,
      relationship: nominee.nominee_relationship,
      gender: nominee.nominee_gender,
      reasonNonBloodRelation: isReasonRequired ? (nominee.nominee_declaration ?? "") : "",
      otherReasonForNonBloodRelation:
        isReasonRequired && nominee.nominee_declaration === OPTION_VALUES.NON_BLOOD_RELATION_REASON.OTHER
          ? (nominee.nominee_other_reason ?? "")
          : "",
      dob: dobToPartnerFormat ? convertddMMYYToddMmmYyyy(nominee.nominee_dob) : nominee.nominee_dob,
      sharePercentage: nominee.share_percent,
      mobile: nominee.nominee_mobile_number ?? "",
      email: nominee.nominee_email_id ?? "",
    };

    // Add appointee details only if appointee name exists
    if (nominee.appointee_fullname) {
      const appointeeName = splitFullName(nominee.appointee_fullname);

      nomineeInfo.apnteDtls = {
        frstNm: appointeeName?.firstName ?? "",
        lstNm: appointeeName?.lastName ?? "",
        relationship: nominee.appointee_relationship,
        gender: nominee.appointee_gender,
        dob: dobToPartnerFormat ? convertddMMYYToddMmmYyyy(nominee.appointee_dob) : nominee.appointee_dob,
        mobile: nominee.appointee_mobile_number ?? "",
        email: nominee.appointee_email_id ?? "",
      };
    }

    return nomineeInfo;
  });
};

export const getNomineeInfos = ({ pDetails }) => {
  if (pDetails?.under_mwpa) {
    return getEmptyNomineeDetails();
  }
  // if isProposer is true, then set nominee details from proposer
  if (pDetails?.isProposerBool) {
    return getNomineeDetailsFromProposer(pDetails);
  }

  if (pDetails?.multi_nominee) {
    return getMultipleNomineeInfo({ pDetails });
  }

  const isReasonRequired = checkNomineeReasonRequired({
    maritalStatus: pDetails?.marital_status,
    relation: pDetails?.nominee_relationship,
  });

  const nomineeName = splitFullName(pDetails?.nominee_fullname);
  const nomineeInfo = getNomineeDetails(pDetails, nomineeName, isReasonRequired);

  return pDetails?.isNomineeMinorBool
    ? { ...nomineeInfo, apnteDtls: getAppointeeDetails(pDetails) }
    : { ...nomineeInfo, ...getNonMinorNomineeBankDetails(pDetails) };
};

export const getPolicyOptions = () => {
  const allPolicies = [...APP_FORM_OPTIONS.POLICY_COMPANIES, ...APP_FORM_OPTIONS.OTHERS_POLICY_COMPANIES];
  return allPolicies?.filter((pol) => pol.id !== OPTION_VALUES.HEALTH_ILLNESS.OTHERS);
};

export const showProposerIIBFields = () => {
  const {
    appFormData: { personalDetails },
  } = GlobalStore.getSnapshot();
  return [OPTION_VALUES.OCCUPATION.STUDENT, OPTION_VALUES.OCCUPATION.HOUSEWIFE].includes(personalDetails?.occupation);
};

export const updateByKeyValue = ({ data, matchKey, matchValue, updateKey, updateValue }) => {
  const traverse = (obj) => {
    if (typeof obj !== "object" || obj === null) return obj;

    if (obj?.[matchKey] === matchValue) {
      return {
        ...obj,
        [updateKey]: updateValue,
      };
    }

    return Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, traverse(v)]));
  };

  return traverse(data);
};

export const productsWithoutHealtDetails = [];

export const isHideHealthDetails = () => {
  const currentProductMeta = getCurrentProductFromMeta();
  return productsWithoutHealtDetails?.includes(currentProductMeta);
};
export const productsWithoutPayoutDetails = [PRODUCT_KEYS.GIFT_CITY, PRODUCT_KEYS.GLOBAL_WEALTH_ACCELERATOR];

export const isHidePayoutDetails = () => {
  const currentProductMeta = getCurrentProductFromMeta();
  return productsWithoutPayoutDetails?.includes(currentProductMeta);
};

export const isProductPresent = (products) => {
  const currentProductMeta = getCurrentProductFromMeta();
  return products?.includes(currentProductMeta);
};

export const getTobabboPerDayAttribuites = () => {
  const productKey = {
    [PRODUCT_KEYS.GIFT_CITY]: {
      type: INPUT_TYPES.INPUT,
      props: {
        digitsOnly: true,
      },
      schema: (schema) =>
        schema
          .required("Please enter your Tobacco per day")
          .matches(REGEX.NON_ZERO_NUMBER, "Please enter a valid Tobacco per day"),
    },
    [PRODUCT_KEYS.GLOBAL_WEALTH_ACCELERATOR]: {
      type: INPUT_TYPES.INPUT,
      props: {
        digitsOnly: true,
      },
      schema: (schema) =>
        schema
          .required("Please enter your Tobacco per day")
          .matches(REGEX.NON_ZERO_NUMBER, "Please enter a valid Tobacco per day"),
    },
    default: {
      type: INPUT_TYPES.DROPDOWN,
      props: {
        options: SMART_KID_OPTIONS.TOBACCO_QUANTITY_OPTIONS,
        empty: t("savingsAppForm.placeholders.select_quantity"),
      },
      schema: (schema) => schema.required(t("savingsAppForm.validation_errors.tobacco_per_day_required")),
    },
  };

  return productKey[getCurrentProductFromMeta()] || productKey.default;
};

export const getAlcoholPerDayAttributes = () => {
  const productKey = {
    [PRODUCT_KEYS.GIFT_CITY]: {
      type: INPUT_TYPES.INPUT,
      props: {
        digitsOnly: true,
      },
      schema: (schema) =>
        schema
          .required("Please enter your Alcohol per day")
          .matches(REGEX.NON_ZERO_NUMBER, "Please enter a valid Alcohol per day"),
    },
    [PRODUCT_KEYS.GLOBAL_WEALTH_ACCELERATOR]: {
      type: INPUT_TYPES.INPUT,
      props: {
        digitsOnly: true,
      },
      schema: (schema) =>
        schema
          .required("Please enter your Alcohol per day")
          .matches(REGEX.NON_ZERO_NUMBER, "Please enter a valid Alcohol per day"),
    },
    default: {
      type: INPUT_TYPES.DROPDOWN,
      props: {
        options: SMART_KID_OPTIONS.ALCOHOL_QUANTITY_OPTIONS,
        empty: t("savingsAppForm.placeholders.select_quantity"),
      },
      schema: (schema) => schema.required(t("savingsAppForm.validation_errors.alcohol_per_day_required")),
    },
  };

  return productKey[getCurrentProductFromMeta()] || productKey.default;
};

export const formatOptions = (options) => {
  return Object.values(options).map((item) => ({ id: item.jsonName, name: item.uiName }));
};

export const isHideTobaccoAbsistence = () => {
  const visibleProducts = [PRODUCT_KEYS.GIFT_CITY, PRODUCT_KEYS.GLOBAL_WEALTH_ACCELERATOR];
  return !isProductPresent(visibleProducts);
};

export const isHideAlcoholAbsistence = () => {
  const visibleProducts = [PRODUCT_KEYS.GIFT_CITY, PRODUCT_KEYS.GLOBAL_WEALTH_ACCELERATOR];
  return !isProductPresent(visibleProducts);
};

export const isHideNarcoticsConsumed = () => {
  const visibleProducts = [PRODUCT_KEYS.GIFT_CITY, PRODUCT_KEYS.GLOBAL_WEALTH_ACCELERATOR];
  return !isProductPresent(visibleProducts);
};

export const isHideNarcoticsQuantity = () => {
  const visibleProducts = [PRODUCT_KEYS.GIFT_CITY, PRODUCT_KEYS.GLOBAL_WEALTH_ACCELERATOR];
  return !isProductPresent(visibleProducts);
};

export const isHideNarcoticsYears = () => {
  const visibleProducts = [PRODUCT_KEYS.GIFT_CITY, PRODUCT_KEYS.GLOBAL_WEALTH_ACCELERATOR];
  return !isProductPresent(visibleProducts);
};

export const isHideNarcoticsAbsistence = () => {
  const visibleProducts = [PRODUCT_KEYS.GIFT_CITY, PRODUCT_KEYS.GLOBAL_WEALTH_ACCELERATOR];
  return !isProductPresent(visibleProducts);
};

export const isShowBicCode = () => {
  const visibleProducts = [PRODUCT_KEYS.GIFT_CITY, PRODUCT_KEYS.GLOBAL_WEALTH_ACCELERATOR];
  return isProductPresent(visibleProducts);
};

export const addExtraManualPolicyFields = () => {
  const visibleProducts = [PRODUCT_KEYS.GIFT_CITY, PRODUCT_KEYS.GLOBAL_WEALTH_ACCELERATOR];
  return isProductPresent(visibleProducts);
};

export const isShowReasonForPolicyIndex = (option = {}) => {
  if (!addExtraManualPolicyFields()) return false;
  const {
    appFormData: { personalDetails },
  } = GlobalStore.getSnapshot();
  const { currentIndex } = option;
  const value = personalDetails?.pre_insurance_data?.[currentIndex]?.policy_decision;
  return showReasonForFieldsValue?.includes(value);
};

export const isShowPreviousPolicyQuestion1Desc = () => {
  const {
    appFormData: { personalDetails },
  } = GlobalStore.getSnapshot();
  return personalDetails?.previousPolicyQuestion1 === OPTION_VALUES.TRUE;
};

export const isShowPreviousPolicyQuestion2Desc = () => {
  const {
    appFormData: { personalDetails },
  } = GlobalStore.getSnapshot();
  return personalDetails?.previousPolicyQuestion2 === OPTION_VALUES.TRUE;
};

export const isShowSwiftTransfer = () => {
  const visibleProducts = [PRODUCT_KEYS.GIFT_CITY, PRODUCT_KEYS.GLOBAL_WEALTH_ACCELERATOR];
  return isProductPresent(visibleProducts);
};

export const isShowPurposeCode = () => {
  const visibleProducts = [PRODUCT_KEYS.GIFT_CITY, PRODUCT_KEYS.GLOBAL_WEALTH_ACCELERATOR];
  return isProductPresent(visibleProducts);
};

export const getAddressProofOptions = () => {
  const productKey = {
    [PRODUCT_KEYS.GIFT_CITY]: APP_FORM_OPTIONS.IDENTITY_PROOF_GIFT_CITY,
    [PRODUCT_KEYS.GLOBAL_WEALTH_ACCELERATOR]: APP_FORM_OPTIONS.IDENTITY_PROOF_GIFT_CITY,
    default: APP_FORM_OPTIONS.IDENTITY_PROOF,
  };

  return productKey[getCurrentProductFromMeta()] || productKey.default;
};

export const getTobaccoConsumedOptions = () => {
  const productKey = {
    [PRODUCT_KEYS.GIFT_CITY]: SMART_KID_OPTIONS.TOBACCO_AS_OPTIONS_GIFT_CITY,
    [PRODUCT_KEYS.GLOBAL_WEALTH_ACCELERATOR]: SMART_KID_OPTIONS.TOBACCO_AS_OPTIONS_GIFT_CITY,
    default: SMART_KID_OPTIONS.TOBACCO_AS_OPTIONS,
  };

  return productKey[getCurrentProductFromMeta()] || productKey.default;
};

export const getAlcoholConsumedOptions = () => {
  const productKey = {
    [PRODUCT_KEYS.GIFT_CITY]: SMART_KID_OPTIONS.ALCOHOL_AS_OPTIONS_GIFT_CITY,
    [PRODUCT_KEYS.GLOBAL_WEALTH_ACCELERATOR]: SMART_KID_OPTIONS.ALCOHOL_AS_OPTIONS_GIFT_CITY,
    default: SMART_KID_OPTIONS.ALCOHOL_AS_OPTIONS,
  };

  return productKey[getCurrentProductFromMeta()] || productKey.default;
};

export const getIdentityProofOptions = () => {
  const productKey = {
    [PRODUCT_KEYS.GIFT_CITY]: APP_FORM_OPTIONS.IDENTITY_PROOF_GIFT_CITY,
    [PRODUCT_KEYS.GLOBAL_WEALTH_ACCELERATOR]: APP_FORM_OPTIONS.IDENTITY_PROOF_GIFT_CITY,
    default: APP_FORM_OPTIONS.IDENTITY_PROOF,
  };

  return productKey[getCurrentProductFromMeta()] || productKey.default;
};

export const getAgeProofOptions = () => {
  const productKey = {
    [PRODUCT_KEYS.GIFT_CITY]: APP_FORM_OPTIONS.AGE_PROOF_GIFT_CITY,
    [PRODUCT_KEYS.GLOBAL_WEALTH_ACCELERATOR]: APP_FORM_OPTIONS.AGE_PROOF_GIFT_CITY,
    default: APP_FORM_OPTIONS.AGE_PROOF_2,
  };

  return productKey[getCurrentProductFromMeta()] || productKey.default;
};

export const isShowHealthDeclarationForm = () => {
  const visibleProducts = [PRODUCT_KEYS.GIFT_CITY, PRODUCT_KEYS.GLOBAL_WEALTH_ACCELERATOR];
  return isProductPresent(visibleProducts);
};

export const getOccupationOptionsForGiftCity = (options) =>
  options.filter(
    (item) => item.id !== OPTION_VALUES.OCCUPATION.STUDENT && item.id !== OPTION_VALUES.OCCUPATION.HOUSEWIFE
  );

export const getOccupationOptions = (allOptions) => {
  const productKey = {
    [PRODUCT_KEYS.GIFT_CITY]: getOccupationOptionsForGiftCity(allOptions),
    [PRODUCT_KEYS.GLOBAL_WEALTH_ACCELERATOR]: getOccupationOptionsForGiftCity(allOptions),
    default: allOptions,
  };

  return productKey[getCurrentProductFromMeta()] || productKey.default;
};

export const isHideNriAccountBankType = () => {
  const hiddenProducts = [PRODUCT_KEYS.GIFT_CITY, PRODUCT_KEYS.GLOBAL_WEALTH_ACCELERATOR];
  return isProductPresent(hiddenProducts);
};

export const getProceedPaymentContentKey = () => {
  const productKey = {
    [PRODUCT_KEYS.GIFT_CITY]: "gift_city_payment_disclaimer",
    [PRODUCT_KEYS.GLOBAL_WEALTH_ACCELERATOR]: "gift_city_payment_disclaimer",
    default: "default_payment_disclaimer",
  };

  return productKey[getCurrentProductFromMeta()] || productKey.default;
};

export const getPregnancyWeekLabel = (sectionName) => {
  const productKey = {
    [PRODUCT_KEYS.GIFT_CITY]: t(`savingsAppForm.${sectionName}.pregnancy_week_gift_city`),
    [PRODUCT_KEYS.GLOBAL_WEALTH_ACCELERATOR]: t(`savingsAppForm.${sectionName}.pregnancy_week_gift_city`),
    default: t(`savingsAppForm.${sectionName}.pregnancy_week`),
  };

  return productKey[getCurrentProductFromMeta()] || productKey.default;
};

export const getPregnancyWeekOptions = () => {
  const productKey = {
    [PRODUCT_KEYS.GIFT_CITY]: {
      className: "md:col-span-3",
      props: {
        attr: { maxLength: 255 },
      },
      schema: (schema) =>
        schema
          .required("Provide details can not be empty")
          .test("only-one-space", t("savingsAppForm.validation_errors.no_multi_spaces"), validateOnlyOneSpace)
          .test("is-valid-format", t("savingsAppForm.validation_errors.special_char_not_allowed"), (value) =>
            noSpecialCharactersValidator(value)
          )
          .test("is-valid-format", t("savingsAppForm.validation_errors.valid_remark"), (value) =>
            noLeadingTrailingSpaces(value)
          )
          .min(2, t("savingsAppForm.validation_errors.valid_remark")),
    },
    [PRODUCT_KEYS.GLOBAL_WEALTH_ACCELERATOR]: {
      className: "md:col-span-3",
      props: {
        attr: { maxLength: 255 },
      },
      schema: (schema) =>
        schema
          .required("Provide details can not be empty")
          .test("only-one-space", t("savingsAppForm.validation_errors.no_multi_spaces"), validateOnlyOneSpace)
          .test("is-valid-format", t("savingsAppForm.validation_errors.special_char_not_allowed"), (value) =>
            noSpecialCharactersValidator(value)
          )
          .test("is-valid-format", t("savingsAppForm.validation_errors.valid_remark"), (value) =>
            noLeadingTrailingSpaces(value)
          )
          .min(2, t("savingsAppForm.validation_errors.valid_remark")),
    },
    default: {
      className: "",
      props: {
        digitsOnly: true,
        maxLength: 2,
      },
      schema: (schema) =>
        schema
          .required(t("savingsAppForm.validation_errors.weeks_required"))
          .test("is-valid-format", t("savingsAppForm.validation_errors.weeks_invalid"), (value) => {
            return value >= 0 && value <= 42;
          }),
    },
  };

  return productKey[getCurrentProductFromMeta()] || productKey.default;
};

export const getPolicyNameLabel = () => {
  const productKey = {
    [PRODUCT_KEYS.GIFT_CITY]: t("appForm.personal_details.policy_name_insurance_gift_city"),
    [PRODUCT_KEYS.GLOBAL_WEALTH_ACCELERATOR]: t("appForm.personal_details.policy_name_insurance_gift_city"),
    default: t("appForm.personal_details.policy_name_insurance"),
  };

  return productKey[getCurrentProductFromMeta()] || productKey.default;
};

export const getLifeCoverLabel = () => {
  const productKey = {
    [PRODUCT_KEYS.GIFT_CITY]: t("appForm.personal_details.life_cover_assure_amt_gift_city"),
    [PRODUCT_KEYS.GLOBAL_WEALTH_ACCELERATOR]: t("appForm.personal_details.life_cover_assure_amt_gift_city"),
    default: t("appForm.personal_details.life_cover_assure_amt"),
  };

  return productKey[getCurrentProductFromMeta()] || productKey.default;
};

export const getEkycNumber = (value, isNri) => {
  const productKey = {
    [PRODUCT_KEYS.GIFT_CITY]: value,
    [PRODUCT_KEYS.GLOBAL_WEALTH_ACCELERATOR]: value,
    default: isNri ? undefined : value,
  };

  return productKey[getCurrentProductFromMeta()] || productKey.default;
};

export const getEkycflag = (value, isNri) => {
  const productKey = {
    [PRODUCT_KEYS.GIFT_CITY]: value,
    [PRODUCT_KEYS.GLOBAL_WEALTH_ACCELERATOR]: value,
    default: isNri ? undefined : value,
  };

  return productKey[getCurrentProductFromMeta()] || productKey.default;
};

export const getEkycToken = (value, isNri) => {
  const productKey = {
    [PRODUCT_KEYS.GIFT_CITY]: value,
    [PRODUCT_KEYS.GLOBAL_WEALTH_ACCELERATOR]: value,
    default: isNri ? undefined : value || "",
  };

  return productKey[getCurrentProductFromMeta()] || productKey.default;
};

export const getEkycHit = (value, isNri) => {
  const productKey = {
    [PRODUCT_KEYS.GIFT_CITY]: value,
    [PRODUCT_KEYS.GLOBAL_WEALTH_ACCELERATOR]: value,
    default: isNri ? undefined : value,
  };

  return productKey[getCurrentProductFromMeta()] || productKey.default;
};

export const getManualSectionToggleTitle = () => {
  const productKey = {
    [PRODUCT_KEYS.GIFT_CITY]: t("savingsAppForm.previewDetails.previous_policy_gift_city"),
    [PRODUCT_KEYS.GLOBAL_WEALTH_ACCELERATOR]: t("savingsAppForm.previewDetails.previous_policy_gift_city"),
    default: t("savingsAppForm.previewDetails.previous_policy"),
  };

  return productKey[getCurrentProductFromMeta()] || productKey.default;
};

export const isHideIIBToggle = () => {
  const hiddenProducts = [PRODUCT_KEYS.GIFT_CITY, PRODUCT_KEYS.GLOBAL_WEALTH_ACCELERATOR];
  return isProductPresent(hiddenProducts);
};

export const isShowIDNO = () => {
  const visibleProducts = [PRODUCT_KEYS.GIFT_CITY, PRODUCT_KEYS.GLOBAL_WEALTH_ACCELERATOR];
  return isProductPresent(visibleProducts);
};

export const isShowHazardousOccupationFollowUp = () => {
  const visibleProducts = [PRODUCT_KEYS.GIFT_CITY, PRODUCT_KEYS.GLOBAL_WEALTH_ACCELERATOR];
  return isProductPresent(visibleProducts);
};

export const getLifeCoverAssureAmountTooltip = () => {
  const productKey = {
    [PRODUCT_KEYS.GIFT_CITY]: null,
    [PRODUCT_KEYS.GLOBAL_WEALTH_ACCELERATOR]: null,
    default: null,
  };

  return { content: productKey[getCurrentProductFromMeta()] } || productKey.default;
};

export const getAnnualIncomeTooltip = () => {
  const productKey = {
    [PRODUCT_KEYS.GIFT_CITY]: <NriAnnualIncomeTooltipGiftCity />,
    [PRODUCT_KEYS.GLOBAL_WEALTH_ACCELERATOR]: <NriAnnualIncomeTooltipGiftCity />,
    default: <NriAnnualIncomeTooltip />,
  };

  return productKey[getCurrentProductFromMeta()] || productKey.default;
};

export const getPregnantLabel = () => {
  const productKey = {
    [PRODUCT_KEYS.GIFT_CITY]: "pregnant_gift_city",
    [PRODUCT_KEYS.GLOBAL_WEALTH_ACCELERATOR]: "pregnant_gift_city",
    default: "pregnant",
  };

  return productKey[getCurrentProductFromMeta()] || productKey.default;
};

export const getHazardousOccupationLabel = () => {
  const productKey = {
    [PRODUCT_KEYS.GIFT_CITY]: "hazardousOccupationGiftCity",
    [PRODUCT_KEYS.GLOBAL_WEALTH_ACCELERATOR]: "hazardousOccupationGiftCity",
    default: "hazardousOccupation",
  };

  return productKey[getCurrentProductFromMeta()] || productKey.default;
};

export const getInternationalAddressTooltip = () => {
  const productKey = {
    [PRODUCT_KEYS.GIFT_CITY]: <AddressTooltip suffix="international_address_gift_city" />,
    [PRODUCT_KEYS.GLOBAL_WEALTH_ACCELERATOR]: <AddressTooltip suffix="international_address_gift_city" />,
    default: <AddressTooltip suffix="international_address" />,
  };

  return { content: productKey[getCurrentProductFromMeta()] } || productKey.default;
};

export const getIndianAddressTooltipContent = () => {
  const productKey = {
    [PRODUCT_KEYS.GIFT_CITY]: <AddressTooltip suffix="communication_address_gift_city" />,
    [PRODUCT_KEYS.GLOBAL_WEALTH_ACCELERATOR]: <AddressTooltip suffix="communication_address_gift_city" />,
    default: <AddressTooltip suffix="communication_address" />,
  };

  return { content: productKey[getCurrentProductFromMeta()] } || productKey.default;
};

export const mapAllHealthQuestions = () => {
  const allQuestions = isShowHealthDeclarationForm()
    ? allHealthQuestions?.map((item) => ({
        ...item,
        showHealthDeclarationForm: true,
      }))
    : allHealthQuestions;
  const currentProduct = getCurrentProductFromMeta();
  return allQuestions
    ?.filter((item) => {
      if (item?.hiddenProducts) {
        return !item.hiddenProducts.includes(currentProduct);
      }
      if (item?.visibleProducts) {
        return item?.visibleProducts?.includes(currentProduct);
      }
      return true;
    })
    ?.map((item) => {
      if (item.key === "pregnant") {
        return {
          ...item,
          label: getPregnantLabel(),
        };
      }
      if (item.key === "hazardousOccupation") {
        return {
          ...item,
          label: getHazardousOccupationLabel(),
        };
      }
      return item;
    });
};
export const getPartnerPageName = () => {
  const activeLanguageCode = getActiveLanguage()?.code;
  const currentProductDetail = getProductDetail();
  let pageName = currentProductDetail?.name ?? "";
  if (activeLanguageCode !== ENGLISH) {
    pageName = `${pageName} ${LANGUAGES.forPartner?.[activeLanguageCode] || ""}`;
  }

  return pageName;
};

export const isHideAsPerChoiceDropdown = () => {
  const hiddenProducts = [PRODUCT_KEYS.GLOBAL_WEALTH_ACCELERATOR];
  return isProductPresent(hiddenProducts);
};
export const getFundReturnsLabel = () => {
  const productKey = {
    [PRODUCT_KEYS.GLOBAL_WEALTH_ACCELERATOR]: t("fund.fundAllocation.fundReturnWealthAccelerator"),
    default: t("fund.fundAllocation.fundReturn"),
  };

  return productKey[getCurrentProductFromMeta()] || productKey.default;
};

export const shouldValidateFullName = () => {
  const mapper = {
    [PRODUCT_KEYS.GIFT_CITY]: true,
    [PRODUCT_KEYS.GLOBAL_WEALTH_ACCELERATOR]: true,
    default: false,
  };

  const productKey = getCurrentProductFromMeta();
  return mapper[productKey] ?? mapper.default;
};
